/**
 * Deal-related query hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mockDeals, mockDealEvents, mockDealSteps, mockPipeline } from '@/api/mock'
import { dealsApi, pipelineApi } from '@/api/client'
import type { ActionResponse, Deal, DealEvent, DealStep, Pipeline, StageKey } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

export const dealKeys = {
  all: ['deals'] as const,
  list: (stages?: StageKey[]) => ['deals', 'list', stages] as const,
  detail: (id: string) => ['deals', id] as const,
  steps: (id: string) => ['deals', id, 'steps'] as const,
  tasks: (id: string) => ['deals', id, 'tasks'] as const,
  events: (id: string) => ['deals', id, 'events'] as const,
  pipeline: ['pipeline'] as const,
}

// ---- Pipeline ----
// GET /api/pipeline is implemented (pipeline_router.py:73) — real endpoint, not a stub.

export function usePipeline() {
  return useQuery<Pipeline>({
    queryKey: dealKeys.pipeline,
    queryFn: () => (USE_MOCK ? Promise.resolve(mockPipeline) : pipelineApi.get()),
    staleTime: 5 * 60_000,
  })
}

// ---- Deals list (for kanban) ----

export function useDealsList(stages?: StageKey[]) {
  return useQuery<Deal[]>({
    queryKey: dealKeys.list(stages),
    queryFn: () => {
      if (USE_MOCK) {
        const items = stages
          ? mockDeals.filter((d) => stages.includes(d.stage_key))
          : mockDeals
        return Promise.resolve(items)
      }
      return dealsApi.list({ stage: stages }).then((r) => r.items)
    },
    staleTime: 20_000,
    refetchInterval: 60_000,
  })
}

// ---- Deal detail ----

export function useDeal(dealId: string) {
  return useQuery<Deal>({
    queryKey: dealKeys.detail(dealId),
    queryFn: () => {
      if (USE_MOCK) {
        const d = mockDeals.find((x) => x.id === dealId)
        if (!d) return Promise.reject(new Error('Deal not found'))
        return Promise.resolve(d)
      }
      return dealsApi.get(dealId)
    },
    enabled: !!dealId,
  })
}

// ---- Move stage (with gate enforcement) ----

export function useMoveStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealId, stageKey }: { dealId: string; stageKey: StageKey }) => {
      if (USE_MOCK) {
        const d = mockDeals.find((x) => x.id === dealId)
        if (d) d.stage_key = stageKey
        return Promise.resolve(d!)
      }
      return dealsApi.moveStage(dealId, stageKey)
    },
    onSuccess: (_data, { dealId }) => {
      qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) })
      qc.invalidateQueries({ queryKey: dealKeys.list() })
    },
  })
}

// ---- Steps ----

export function useDealSteps(dealId: string) {
  return useQuery<DealStep[]>({
    queryKey: dealKeys.steps(dealId),
    queryFn: () => {
      if (USE_MOCK) {
        return Promise.resolve(mockDealSteps.filter((s) => s.deal_id === dealId))
      }
      return dealsApi.getSteps(dealId)
    },
    enabled: !!dealId,
  })
}

// ---- Deal events (timeline) ----

export function useDealEvents(dealId: string) {
  return useQuery<DealEvent[]>({
    queryKey: dealKeys.events(dealId),
    queryFn: () => {
      if (USE_MOCK) {
        // Return mock events only for deal-003; empty for others
        if (dealId === 'deal-003') return Promise.resolve(mockDealEvents)
        return Promise.resolve([])
      }
      return dealsApi.getEvents(dealId)
    },
    enabled: !!dealId,
    staleTime: 30_000,
  })
}

export function useToggleStep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealId, stepKey, done }: { dealId: string; stepKey: string; done: boolean }) => {
      if (USE_MOCK) {
        const s = mockDealSteps.find((x) => x.step_key === stepKey)
        if (s) { s.done = done; s.done_at = done ? new Date().toISOString() : null }
        return Promise.resolve(s!)
      }
      return dealsApi.toggleStep(dealId, stepKey, done)
    },
    onSuccess: (_data, { dealId }) => {
      qc.invalidateQueries({ queryKey: dealKeys.steps(dealId) })
    },
  })
}

// ---- Action (→ ops_tasks) ----

export function useDealAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      dealId,
      label,
      kind,
      params,
    }: {
      dealId: string
      label: string
      kind: string
      params?: Record<string, unknown>
    }): Promise<ActionResponse> => {
      if (USE_MOCK) {
        // Shape matches the real backend ActionResponse {task_id,kind,status,result}
        // (deals_router.py) — NOT the full OpsTask row — so dev and prod behave the same.
        const action: ActionResponse = {
          task_id: `task-${Date.now()}`,
          kind,
          status: 'queued',
          result: null,
        }
        return Promise.resolve(action)
      }
      return dealsApi.action(dealId, { label, kind, params })
    },
    onSuccess: (_data, { dealId }) => {
      qc.invalidateQueries({ queryKey: dealKeys.tasks(dealId) })
    },
  })
}
